import _round from "lodash/round";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { CopyComponent } from "../../../../parts/CopyComponent";
import MetconDisplay from "../../../../parts/metcon/MetconDisplay";
import { getExerciseName } from "../../../../parts/modals/learn-more/blockHelpers";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import { formattedTarget, getSetCounts } from "../../../../utils/actuals";
import { getComplexSetReps } from "../../../../utils/complex";
import convertToRiR from "../../../../utils/converters/convert-intensity";
import { isPROutcome } from "../../../../utils/personal-records";
import { calculatePrefixForExercise } from "../../../../utils/prefix-calculator";

function MonthExercise({
  exercise,
  supersets,
  units,
  round,
  withWeight,
  fromMetric,
  expanded,
  date = null,
  intensityMeasure,
  isRiR,
  mobile = false,
  showCopyButton = true,
  athlete,
  exercises,
  sectionPrefix,
}) {
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();
  const repMaxPR = locals.dayPersonalRecords?.repMaxPR;

  if (exercises?.length) {
    exercise = exercises[0];
  }
  function hasNumber(myString) {
    return /\d/.test(myString);
  }

  const prefix = (
    sectionPrefix ?? calculatePrefixForExercise(exercise._id, supersets)
  ).replace(")", "");

  // assumption is set.load is always stored as KG; so convert to user's unit
  const precision = units === "metric" ? 2 : 1;
  const isMetcon = exercise.primary === "metcon";

  return (
    <div
      className={`exercise-element ${hasNumber(prefix) ? "is-superset" : ""}`}
      onClick={() => {
        if (date) {
          submit("/actions/refresh", {
            method: "GET",
            body: {
              view: "day",
              date,
              relTrends: true,
            },
          });
        }
      }}
    >
      <div className="exercise-element-title justify-between">
        <div className="flex items-center gap-2">
          <span className="calendar-prefix">{prefix}</span>
          <p>{getExerciseName(exercise)}</p>
        </div>
        {showCopyButton && (
          <CopyComponent exercises={exercises || exercise} athlete={athlete} />
        )}
      </div>
      {isMetcon && <MetconDisplay exercise={exercise} compact />}
      {(() => {
        const shownPRReps = new Set();
        return exercise.sets.map((elem, idx) => {
          const { total, completed, missed } = getSetCounts(exercise, elem);
          const load = elem.load
            ? _round(fromMetric(elem.load, units), precision)
            : 0;
          const targetLoad = !!load && withWeight(load, units);
          let complexReps;
          if (exercises?.length)
            complexReps = getComplexSetReps(exercises, idx);
          return (
            <div
              className="exercise-element-set-wrapper"
              key={`${exercise._id}${idx}`}
            >
              <div
                className={`exercise-element-set${expanded ? " expanded" : ""}`}
              >
                <span
                  className={
                    completed + missed === total ||
                    exercise.metconResult?.status === "completed"
                      ? "dot bg-(--success)"
                      : missed === 0 && completed === 0
                        ? "dot bg-(--white-20)"
                        : "dot bg-(--information)"
                  }
                ></span>
                {formattedTarget(
                  elem,
                  exercise.type === "primary" || exercise.type === "secondary",
                  elem.load ? `${_round(elem.percentRepMax, 1)}` : "",
                  "metric",
                  targetLoad,
                  intensityMeasure,
                  isRiR,
                  false,
                  complexReps
                )}
                {(() => {
                  if (!repMaxPR || !elem.outcomes) return null;
                  const pr = Object.values(elem.outcomes).find((o) => {
                    const result = isPROutcome(exercise, o, repMaxPR, date);
                    return result !== null && !shownPRReps.has(result.reps);
                  });
                  if (!pr) return null;
                  const result = isPROutcome(exercise, pr, repMaxPR, date);
                  shownPRReps.add(result.reps);
                  return <span className="pr-star">&#9733;</span>;
                })()}
              </div>
              {mobile && elem.outcomes && (
                <div className="exercise-details-wrapper">
                  <div className="exercise-headers-wrapper">
                    <span className="exercise-stat header">
                      {t("calendar.monthExercise.actualResults")}
                    </span>
                    <span className="exercise-stat header">
                      {t("calendar.actuals.setNumber")}
                    </span>
                    <span className="exercise-stat header">
                      {t("calendar.monthExercise.reps")}
                    </span>
                    <span className="exercise-stat header">
                      {isRiR
                        ? t("calendar.monthExercise.rir")
                        : t("calendar.monthExercise.rpe")}
                    </span>
                    <span className="exercise-stat header">
                      {t("calendar.monthExercise.load")}
                    </span>
                  </div>
                  {elem.outcomes &&
                    Object.keys(elem.outcomes).length > 0 &&
                    Object.keys(elem.outcomes).map((key, idy) => {
                      return (
                        <div
                          className="exercise-stats-wrapper"
                          key={`${idx}${idy}`}
                        >
                          <span className="exercise-stat">
                            {elem.outcomes[key].status === "completed" ? (
                              <img
                                src="/img/svg/check.svg"
                                style={{
                                  filter: "var(--green-filter)",
                                  width: "12px",
                                }}
                              />
                            ) : elem.outcomes[key].status === "missed" ? (
                              <img
                                src="/img/svg/priority_high_black.svg"
                                style={{
                                  filter: "var(--red-filter)",
                                  width: "13px",
                                }}
                              />
                            ) : null}
                          </span>

                          <span className="exercise-stat">
                            {Number(key) + 1}
                          </span>
                          <span className="exercise-stat">
                            {elem.outcomes[key].outcome.reps}
                          </span>
                          <span className="exercise-stat">
                            {elem.outcomes[key].outcome.rpe
                              ? ` ${convertToRiR(
                                  elem.outcomes[key].outcome.rpe,
                                  !isRiR
                                )}`
                              : ""}
                          </span>
                          <span className="exercise-stat">
                            {elem.outcomes[key].outcome.load}
                          </span>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        });
      })()}
    </div>
  );
}

export default MonthExercise;
